import { F0Alert, F0Box, F0Card, F0Text } from '@factorialco/f0-react'
import { Spinner } from '@factorialco/f0-react/dist/experimental'
import { TrainingDetailFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import DropzoneBox from 'modules/documents/components/Documents/DropzoneBox'
import { ScormIframeDialog } from 'modules/trainings/components/ScormIframeDialog/ScormIframeDialog'
import { useScormPackagePreviewLink } from 'modules/trainings/hooks/ScormPackage/useScormPackagePreviewLink'
import { useScormPackageStatus } from 'modules/trainings/hooks/ScormPackage/useScormPackageStatus'
import { useScormPackageUpload } from 'modules/trainings/hooks/ScormPackage/useScormPackageUpload'
import { useScormThumbnailPicker } from 'modules/trainings/hooks/ScormPackage/useScormThumbnailPicker'
import ScormThumbnailPicker from './ScormThumbnailPicker'

export type ScormPackage = NonNullable<TrainingDetailFragment['scormPackage']>

interface Props {
  trainingId: string
  companyId: string
  scormPackage: ScormPackage | null | undefined
  trainingThumbnail?: string | null
  onUploadSuccess?: () => void
}

const IMPORT_STATUS_PENDING = 'pending'
const IMPORT_STATUS_RUNNING = 'running'
const IMPORT_STATUS_COMPLETE = 'complete'
const IMPORT_STATUS_ERROR = 'error'

const isProcessing = (status: string) =>
  [IMPORT_STATUS_PENDING, IMPORT_STATUS_RUNNING].includes(status.toLowerCase())

const isComplete = (status: string) => status.toLowerCase() === IMPORT_STATUS_COMPLETE

const isError = (status: string) => status.toLowerCase() === IMPORT_STATUS_ERROR

const SpinnerCard = ({ label }: { label: string }) => (
  <F0Card compact>
    <F0Box display="flex" flexDirection="row" gap="sm" alignItems="center" justifyContent="center">
      <Spinner />
      <F0Text content={label} variant="body" />
    </F0Box>
  </F0Card>
)

const ScormPackageUpload = ({
  trainingId,
  companyId,
  scormPackage,
  trainingThumbnail,
  onUploadSuccess,
}: Props) => {
  const {
    upload,
    uploading,
    uploadState,
    error: uploadError,
    reset,
  } = useScormPackageUpload({
    trainingId,
    companyId,
    onSuccess: onUploadSuccess,
  })

  useScormPackageStatus({
    scormPackageId: scormPackage?.id,
    importStatus: scormPackage?.importStatus,
    skip: !scormPackage,
  })

  const {
    openPreview,
    closePreview,
    previewUrl,
    loading: previewLoading,
    error: previewError,
  } = useScormPackagePreviewLink({
    scormPackageId: scormPackage?.id,
  })

  const {
    openPicker,
    closePicker,
    pickThumbnail,
    candidateUrls,
    isOpen: isPickerOpen,
    error: pickerError,
    loadingCandidates,
    settingThumbnail,
    thumbnailSet,
  } = useScormThumbnailPicker({
    scormPackageId: scormPackage?.id,
    trainingId: trainingId,
    onThumbnailSet: onUploadSuccess,
  })

  if (uploading) {
    return <SpinnerCard label={i18n.t('trainings.scorm.upload.uploading')} />
  }

  if (scormPackage && isProcessing(scormPackage.importStatus)) {
    return <SpinnerCard label={i18n.t('trainings.scorm.upload.processing')} />
  }

  if (scormPackage && isComplete(scormPackage.importStatus)) {
    return (
      <F0Box display="flex" flexDirection="column" gap="sm">
        {previewError && <F0Alert variant="critical" title={previewError} description="" />}
        {pickerError && !isPickerOpen && (
          <F0Alert variant="critical" title={pickerError} description="" />
        )}
        <F0Card
          compact
          title={scormPackage.title ?? i18n.t('trainings.scorm.upload.complete')}
          description={scormPackage.scormVersion ?? undefined}
          primaryAction={{
            label: previewLoading
              ? i18n.t('trainings.scorm.preview.loading')
              : i18n.t('trainings.scorm.preview.launch'),
            onClick: previewLoading ? () => {} : () => void openPreview(),
          }}
          {...(!thumbnailSet &&
            !trainingThumbnail && {
              secondaryActions: [
                {
                  label: loadingCandidates
                    ? i18n.t('trainings.scorm.thumbnail.loading')
                    : i18n.t('trainings.scorm.thumbnail.pick'),
                  onClick: () => void openPicker(),
                },
              ],
            })}
        />
        <ScormIframeDialog
          isOpen={!!previewUrl}
          onClose={closePreview}
          src={previewUrl}
          title={scormPackage.title ?? i18n.t('trainings.scorm.preview.launch')}
          description={i18n.t('trainings.scorm.preview.ad_blocker_warning')}
        />
        <ScormThumbnailPicker
          isOpen={isPickerOpen}
          candidateUrls={candidateUrls}
          settingThumbnail={settingThumbnail}
          error={pickerError}
          onPick={(index) => void pickThumbnail(index)}
          onClose={closePicker}
        />
      </F0Box>
    )
  }

  if ((scormPackage && isError(scormPackage.importStatus)) || uploadState === 'error') {
    const errorDetail = scormPackage?.importError ?? uploadError
    return (
      <F0Box display="flex" flexDirection="column" gap="md" width="full">
        <F0Alert
          variant="critical"
          title={i18n.t('trainings.scorm.upload.error')}
          description={errorDetail ?? ''}
        />
        <DropzoneBox
          accept={{
            'application/zip': ['.zip'],
            'application/x-zip-compressed': ['.zip'],
          }}
          message={i18n.t('trainings.scorm.upload.retry')}
          multiple={false}
          onClick={reset}
          onDrop={(files) => {
            const file = files[0]
            if (file) void upload(file)
          }}
        />
      </F0Box>
    )
  }

  return (
    <DropzoneBox
      accept={{
        'application/zip': ['.zip'],
        'application/x-zip-compressed': ['.zip'],
      }}
      iconName="file-upload"
      message={i18n.t('trainings.scorm.upload.title')}
      description={i18n.t('trainings.scorm.upload.description')}
      multiple={false}
      disabled={uploading}
      onDrop={(files) => {
        const file = files[0]
        if (file) void upload(file)
      }}
    />
  )
}

export default ScormPackageUpload
